import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AlertNotification from './AlertNotification';
import notificationService from '../../services/notificationService';
import './AlertsContainer.css';

const AlertsContainer = () => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [wsConnection, setWsConnection] = useState(null);

    useEffect(() => {
        if (!user) return;

        // Request notification permission on mount
        notificationService.requestPermission();

        // Set up WebSocket connection for real-time alerts
        const setupWebSocket = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = `${protocol}//${window.location.host}/ws`;
            
            try {
                const ws = new WebSocket(wsUrl);
                
                ws.onopen = () => {
                    console.log('WebSocket connected for price alerts');
                    // Send authentication
                    ws.send(JSON.stringify({
                        type: 'auth',
                        token: localStorage.getItem('token')
                    }));
                };

                ws.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        
                        if (message.type === 'price_alert' && message.data) {
                            handlePriceAlert(message.data);
                        }
                    } catch (error) {
                        console.error('Error parsing WebSocket message:', error);
                    }
                };

                ws.onclose = () => {
                    console.log('WebSocket disconnected, attempting to reconnect...');
                    // Reconnect after 5 seconds
                    setTimeout(setupWebSocket, 5000);
                };

                ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                };

                setWsConnection(ws);
            } catch (error) {
                console.error('Failed to establish WebSocket connection:', error);
            }
        };

        setupWebSocket();

        return () => {
            if (wsConnection) {
                wsConnection.close();
            }
        };
    }, [user]);

    const handlePriceAlert = (alertData) => {
        // Send browser notification
        notificationService.sendPriceAlert(alertData);

        // Add in-app notification
        const notification = {
            id: `alert-${alertData.alertId}-${Date.now()}`,
            ...alertData,
            timestamp: new Date().toISOString()
        };

        setNotifications(prev => [notification, ...prev.slice(0, 3)]); // Keep max 4 notifications

        // Auto-remove after 10 seconds if not manually closed
        setTimeout(() => {
            removeNotification(notification.id);
        }, 10000);
    };

    const removeNotification = (notificationId) => {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
    };

    const handleViewToken = (tokenAddress) => {
        // Navigate to token details or trading page
        const currentPath = window.location.pathname;
        if (currentPath.includes('/tokens') || currentPath === '/') {
            // If already on tokens page, just scroll to token or highlight it
            const tokenElement = document.querySelector(`[data-token-address="${tokenAddress}"]`);
            if (tokenElement) {
                tokenElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                tokenElement.classList.add('highlighted');
                setTimeout(() => {
                    tokenElement.classList.remove('highlighted');
                }, 3000);
            }
        } else {
            // Navigate to tokens page with token parameter
            window.location.href = `/?token=${tokenAddress}`;
        }
    };

    if (!user) return null;

    return (
        <div className="alerts-container">
            {notifications.map(notification => (
                <AlertNotification
                    key={notification.id}
                    alert={notification}
                    onClose={removeNotification}
                    onViewToken={handleViewToken}
                    autoClose={true}
                    duration={8000}
                />
            ))}
        </div>
    );
};

export default AlertsContainer;